import HighchartsReact from 'highcharts-react-official';
import { getChartActions, getChartExportingOptions } from 'modules/productAnalytics/utils';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from 'ui/skeleton';
import { HEIGHT_WITHOUT_CONVERSIONS, HEIGHT_WITH_CONVERSIONS } from '../constants';
import { selectIsLoading } from '../selectors';
import { ConversionEventMetricType, LocalConversionEventStateType } from '../types';
import { ConversionsEmptyCard } from './ConversionsEmptyCard';
import { ConversionsLineChart } from './ConversionsLineChart';
import { ConversionMetricList } from './ConversionsMetricsList';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import {
  ConversionsCardContainer,
  ConversionsCardText,
  ConversionsRow,
  LeftCol,
  RightCol,
  SkeletonWrapper,
} from './Styled';

export interface ConversionsCardProps {
  conversions: ConversionEventMetricType[];
  conversionRateLabel: string;
  conversionRateDividend: number;
}

const addSelectionState = (
  conversions: ConversionEventMetricType[],
): LocalConversionEventStateType[] =>
  conversions.map((conversion) => ({ ...conversion, selected: true }));

export const ConversionsCard = ({
  conversions = [],
  conversionRateLabel,
  conversionRateDividend,
}: ConversionsCardProps) => {
  const isLoading = useSelector(selectIsLoading);
  const [localConversionState, setLocalConversionState] = useState(addSelectionState(conversions));
  const chartRef = useRef<HighchartsReact>(null);
  const chartId = 'conversionChart';
  const chartActions = getChartActions(chartId, chartRef);
  const isConversionsAvailable = !!Object.keys(conversions).length;
  const cardHeight =
    !isLoading && !isConversionsAvailable ? HEIGHT_WITHOUT_CONVERSIONS : HEIGHT_WITH_CONVERSIONS;

  useEffect(() => {
    setLocalConversionState(addSelectionState(conversions));
  }, [isLoading]);

  const toggleConversionSelection = (key: string) => {
    setLocalConversionState(
      localConversionState.map((conversion) =>
        conversion.eventName === key
          ? {
              ...conversion,
              selected: !conversion.selected,
            }
          : conversion,
      ),
    );
  };

  return (
    <MetricBreakdownCard
      title={'Conversions'}
      data-test="conversions-card"
      menuOptions={isConversionsAvailable ? getChartExportingOptions(chartId) : undefined}
      // @ts-expect-error
      handleSelectionChange={isConversionsAvailable && ((key) => chartActions[key]())}
    >
      {isLoading ? (
        <SkeletonWrapper height={cardHeight} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      ) : (
        <ConversionsCardContainer>
          {isConversionsAvailable ? (
            <ConversionsRow>
              <LeftCol>
                <ConversionsCardText>Totals</ConversionsCardText>
                <ConversionMetricList
                  conversions={localConversionState}
                  conversionRateLabel={conversionRateLabel}
                  conversionRateDividend={conversionRateDividend}
                  toggleConversionSelection={(key) => toggleConversionSelection(key)}
                />
              </LeftCol>
              <RightCol>
                <ConversionsCardText>Conversions over time</ConversionsCardText>
                <ConversionsLineChart conversions={localConversionState} innerRef={chartRef} />
              </RightCol>
            </ConversionsRow>
          ) : (
            <ConversionsEmptyCard />
          )}
        </ConversionsCardContainer>
      )}
    </MetricBreakdownCard>
  );
};
